import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/pages/works/components/WorkCard";
import { workTypes, workYears, worksList } from "@/mocks/works";

const ALL = "全部";
const STEP = 4;

export default function WorksList() {
  const [params, setParams] = useSearchParams();
  const [visible, setVisible] = useState(6);
  const [keywordInput, setKeywordInput] = useState(params.get("q") ?? "");

  const keyword = params.get("q") ?? "";
  const type = params.get("type") ?? ALL;
  const year = params.get("year") ?? ALL;
  const sort = params.get("sort") ?? "latest";

  usePageMeta({
    title: "作品展示 - 张津瑜品牌作品集",
    description: "张津瑜品牌作品集，涵盖视觉大片、品牌短片、公益影像等，展示作品风采与项目背景介绍。",
    keywords: "张津瑜作品,作品集,视觉大片,品牌短片,公益影像",
  });

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = [...worksList];
    if (type !== ALL) list = list.filter((w) => w.type === type);
    if (year !== ALL) list = list.filter((w) => w.year === year);
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter(
        (w) =>
          w.title.toLowerCase().includes(kw) ||
          w.summary.toLowerCase().includes(kw) ||
          w.type.toLowerCase().includes(kw),
      );
    }
    list.sort((a, b) =>
      sort === "hot" ? b.views - a.views : a.date < b.date ? 1 : -1,
    );
    return list;
  }, [type, year, keyword, sort]);

  useEffect(() => {
    setVisible(6);
  }, [type, year, keyword, sort]);

  useEffect(() => {
    setKeywordInput(params.get("q") ?? "");
  }, [params]);

  const hasFilter = type !== ALL || year !== ALL || keyword.trim() !== "" || sort !== "latest";
  const resetFilters = () => {
    setKeywordInput("");
    setParams(new URLSearchParams(), { replace: true });
  };

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <>
      <section className="bg-primary-800 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="[&_a]:text-background-300 [&_a:hover]:text-accent-400 [&_span]:text-background-100">
            <Breadcrumb items={[{ label: "作品展示" }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            作品展示
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            这里收录了张津瑜在视觉大片、品牌短片、公益影像与时尚企划方向的重点作品，点击卡片即可查看完整的项目背景与图集。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-8 md:py-12">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateParams({ q: keywordInput.trim() || null });
            }}
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="搜索作品标题或关键词…"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索作品
            </button>
          </form>

          <div className="mt-6 flex flex-col gap-4 border-y border-background-200 py-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">类型：</span>
              {[ALL, ...workTypes].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => updateParams({ type: t === ALL ? null : t })}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    type === t
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">年份：</span>
              {[ALL, ...workYears].map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => updateParams({ year: y === ALL ? null : y })}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    year === y
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {y}
                </button>
              ))}
              <div className="ml-auto flex rounded-md border border-background-300 bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => updateParams({ sort: null })}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                    sort === "latest"
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  最新
                </button>
                <button
                  type="button"
                  onClick={() => updateParams({ sort: "hot" })}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                    sort === "hot"
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  热门
                </button>
              </div>
            </div>
          </div>

          {keyword.trim() && (
            <p className="mt-5 text-sm text-foreground-600">
              搜索关键词：“<span className="font-medium text-primary-600">{keyword}</span>”，
              共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 件作品
            </p>
          )}

          {shown.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
              {hasMore ? (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + STEP)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-add-line text-base" />
                    加载更多作品
                  </button>
                </div>
              ) : (
                <p className="mt-10 text-center text-sm text-foreground-500">
                  已经到底啦，共 {filtered.length} 件作品
                </p>
              )}
            </>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-gallery-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                没有找到相关作品
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试更换关键词，或者重置筛选条件查看全部作品。
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                {hasFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置筛选
                  </button>
                )}
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}